/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t, Trans } from '@lingui/macro'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { MenuItem } from '@mui/material'
import React, { FunctionComponent, useState } from 'react'
import { CriteriaType, CriterionNode } from '../../../state'
import { DropDownMenu } from './CohortCriterionComponent'
import { CriteriaSelectionMenu } from './CriteriaSelectionMenu'

export interface CohortCriterionOptionsMenuProps {
    node: CriterionNode
    editable: boolean
    dragStateEnabled: boolean
    handleCriteriaEdit: () => void
    handleCriteriaCopy: () => void
    handleCriteriaDelete: () => void
    handleAddRelateCriteria?: (criteriaType: CriteriaType) => void
    handleAddAndCriteria?: (criteriaType: CriteriaType) => void
    showRelateOption?: boolean
}

/**
 * Options menu for cohort criteria
 */
export const CohortCriterionOptionsMenu: FunctionComponent<CohortCriterionOptionsMenuProps> = ({
    node,
    editable,
    dragStateEnabled,
    handleCriteriaEdit,
    handleCriteriaCopy,
    handleCriteriaDelete,
    handleAddRelateCriteria,
    handleAddAndCriteria,
    showRelateOption = false
}) => {
    const [relateMenuAnchor, setRelateMenuAnchor] = useState<HTMLElement | null>(null)
    const [andMenuAnchor, setAndMenuAnchor] = useState<HTMLElement | null>(null)
    const relateMenuOpen = Boolean(relateMenuAnchor)
    const andMenuOpen = Boolean(andMenuAnchor)

    const handleRelateMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setRelateMenuAnchor(event.currentTarget)
    }
    const handleRelateMenuClose = () => setRelateMenuAnchor(null)
    const handleAndMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAndMenuAnchor(event.currentTarget)
    }
    const handleAndMenuClose = () => setAndMenuAnchor(null)

    if (!editable) {
        return null
    }

    if (dragStateEnabled) {
        return (
            <>
                <DropDownMenu
                    toggleId={`criteria-menu-toggle-${node.id}`}
                    toggleTestId='criteria-options-menu-toggle'
                    toggleIcon={<MoreHorizIcon />}
                    toggleLabel={t`Criteria Options`}
                    menuId={`criteria-menu-${node.id}`}
                    menuTestId='criteria-options-menu'
                >
                    {/* //Ehr Notes shouldnt have edit menu */}
                    {node.type !== 'EHR_NOTES' && (
                        <MenuItem onClick={handleCriteriaEdit} sx={{ gap: 0.5 }}>
                            <EditOutlinedIcon />
                            <Trans>Edit</Trans>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleCriteriaCopy} sx={{ gap: 0.5 }}>
                        <ContentCopyOutlinedIcon />
                        <Trans>Copy</Trans>
                    </MenuItem>
                    {showRelateOption && handleAddRelateCriteria && (
                        <MenuItem onClick={handleRelateMenuOpen} sx={{ gap: 0.5 }}>
                            <AddCircleIcon />
                            <Trans>Relate</Trans>
                        </MenuItem>
                    )}
                    {handleAddAndCriteria && (
                        <MenuItem onClick={handleAndMenuOpen} sx={{ gap: 0.5 }}>
                            <AddCircleIcon />
                            <Trans>AND</Trans>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleCriteriaDelete} sx={{ gap: 0.5 }}>
                        <DeleteOutlineIcon />
                        <Trans>Delete</Trans>
                    </MenuItem>
                </DropDownMenu>

                {/* RELATE Submenu */}
                {showRelateOption && handleAddRelateCriteria && (
                    <CriteriaSelectionMenu
                        anchorEl={relateMenuAnchor}
                        open={relateMenuOpen}
                        onClose={handleRelateMenuClose}
                        onCriteriaSelect={handleAddRelateCriteria}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        testIdPrefix='criteria-relate-menu-item'
                    />
                )}

                {/* AND Submenu */}
                {handleAddAndCriteria && (
                    <CriteriaSelectionMenu
                        anchorEl={andMenuAnchor}
                        open={andMenuOpen}
                        onClose={handleAndMenuClose}
                        onCriteriaSelect={handleAddAndCriteria}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        testIdPrefix='criteria-and-menu-item'
                    />
                )}
            </>
        )
    } else {
        return (
            <DropDownMenu
                toggleId={`criteria-menu-toggle-${node.id}`}
                toggleTestId='criteria-options-menu-toggle'
                toggleIcon={<MoreHorizIcon />}
                toggleLabel={t`Criteria Options`}
                menuId={`criteria-menu-${node.id}`}
                menuTestId='criteria-options-menu'
            >
                <MenuItem onClick={handleCriteriaEdit} sx={{ gap: 0.5 }}>
                    <EditOutlinedIcon />
                    <Trans>Edit</Trans>
                </MenuItem>
            </DropDownMenu>
        )
    }
}
